import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format, parse } from 'date-fns';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Scale,
  AlertCircle,
  Download,
  Upload,
  CheckCircle,
  Loader2,
  ArrowLeft,
  Send,
  Info,
  MapPin,
  ExternalLink,
  Calendar as CalendarIcon,
  RefreshCw,
  Mail,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Constantes locales (clonadas del formulario logueado para mantener paridad UX)
const LOMAS_MAPA_URL =
  'https://customer-assets-agu9un31.emergentagent.net/job_6fd29e97-dc82-4c7e-911a-17c413733f48/artifacts/38vdd2aa_Captura%20de%20pantalla%202026-09-02%20000042.png';
const LOMAS_GOOGLE_MAPS_URL =
  'https://www.google.com/maps/d/u/0/edit?mid=1ZeoVvURjHb8BJfouHMr7LMMTu5DKWEs&usp=sharing';
const LOMAS_PERIMETRO_CALLES =
  'Uriarte, Carlos Glade, Av. Talleres, Martín Rodríguez, Falucho, Molina Arrotea, General Frías, Garibaldi, Av. Hipólito Irigoyen, 9 de Julio, Pasco, Vélez Sarsfield, Amero, Monseñor Schell, Arenales y Malabia hasta las vías del FFCC Roca.';
const DEPTO_LOMAS = 'Lomas de Zamora';

const TITULOS_HABILITANTES = [
  'Licenciado/a en Trabajo Social',
  'Licenciado/a en Servicio Social',
  'Trabajador Social',
  'Asistente Social',
];

const DEPARTAMENTOS_JUDICIALES = ['Lomas de Zamora', 'Avellaneda-Lanús'];
const MAX_DEPARTAMENTOS = 2;
const DOM_LEGAL_VACIO = { domicilio: '', cp: '', localidad: '', tel_codigo_area: '', tel_numero: '' };

function SelectorDepartamentos({ value = [], onChange, disabled = false }) {
  const toggle = (dep) => {
    if (disabled) return;
    if (value.includes(dep)) {
      onChange(value.filter((d) => d !== dep));
    } else {
      if (value.length >= MAX_DEPARTAMENTOS) return;
      onChange([...value, dep]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {DEPARTAMENTOS_JUDICIALES.map((dep) => {
        const selected = value.includes(dep);
        return (
          <button
            key={dep}
            type="button"
            onClick={() => toggle(dep)}
            disabled={disabled}
            className={`px-3 py-2 rounded-lg border text-sm transition ${
              selected
                ? 'bg-amber-100 border-amber-500 text-amber-900 font-medium'
                : 'bg-white border-gray-300 hover:border-amber-400'
            }`}
            data-testid={`toggle-depto-${dep.replace(/\s+/g, '-')}`}
          >
            {selected ? '✓ ' : ''}
            {dep}
          </button>
        );
      })}
    </div>
  );
}

const INITIAL_FORMDATA = {
  apellido: '',
  nombres: '',
  nacionalidad: 'Argentina',
  documento: '',
  domicilio_profesional: '',
  cp_profesional: '',
  localidad_profesional: '',
  tel_profesional: '',
  domicilio_real: '',
  tel_real_codigo_area: '',
  tel_real_numero: '',
  email_personal: '',
  titulo_habilitante: '',
  fecha_graduacion: '',
  fuero_civil: false,
  fuero_laboral: false,
  domicilio_electronico_corte: '',
  lugar_fecha_curso_procesal: '',
  documentacion_acompanada: '',
  tipo_inscripcion: '',
  departamentos_judiciales: [],
  domicilios_legales: {},
};

export default function OtrosDistritosFormularioPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // iter194: modo corrección (`?corregir=<token>`): el usuario corrige una
  // solicitud observada con `punto_reinicio_observacion=inicio_formulario`.
  const correccionToken = searchParams.get('corregir') || '';
  const esCorreccion = Boolean(correccionToken);
  const [correccionMotivo, setCorreccionMotivo] = useState('');
  const [distritoOrigen, setDistritoOrigen] = useState('');
  const [paso, setPaso] = useState(1); // 1 = formulario, 2 = subir docs
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tokenCorreccion, setTokenCorreccion] = useState('');
  const [error, setError] = useState(null);
  const [camposFaltantesModal, setCamposFaltantesModal] = useState({ open: false, campos: [] });
  const [mapaLomasOpen, setMapaLomasOpen] = useState(false);

  // iter188: info del borrador público (link + código corto) para que el
  // usuario pueda retomar desde otro dispositivo.
  const [borradorInfo, setBorradorInfo] = useState(() => {
    // Precargar desde localStorage por si volvió al mismo browser.
    try {
      const raw = localStorage.getItem('aux_otro_distrito_borrador');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [formData, setFormData] = useState(INITIAL_FORMDATA);
  const [cursoEsteAnio, setCursoEsteAnio] = useState(false);

  const [firmadosPorDep, setFirmadosPorDep] = useState({});
  const [certificado, setCertificado] = useState(null);
  const [copiaDni, setCopiaDni] = useState(null);
  const [sinCertificadoAun, setSinCertificadoAun] = useState(false);

  useEffect(() => {
    const dist = sessionStorage.getItem('aux_otro_distrito_origen') || '';
    if (!dist) {
      navigate('/auxiliares-justicia/otros-distritos', { replace: true });
    } else {
      setDistritoOrigen(dist);
    }
  }, [navigate]);

  // iter194: Si venimos del flujo de corrección pública (?corregir=<token>),
  // cargar el motivo de la observación para mostrarlo arriba del formulario.
  useEffect(() => {
    if (!esCorreccion) return;
    try {
      const raw = localStorage.getItem('aux_otro_distrito_correccion');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.motivo) setCorreccionMotivo(parsed.motivo);
      }
    } catch { /* no-op */ }
  }, [esCorreccion]);

  // iter188: Precargar formData desde localStorage si existe (misma sesión/mismo browser).
  // iter191: Si el borrador trae `resumir_paso=2` (viene del flujo retomar), saltar directo
  // al paso 2 de carga de adjuntos.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('aux_otro_distrito_formdata');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.formData) setFormData((prev) => ({ ...prev, ...parsed.formData }));
        if (typeof parsed?.cursoEsteAnio === 'boolean') setCursoEsteAnio(parsed.cursoEsteAnio);
      }
      const borrRaw = localStorage.getItem('aux_otro_distrito_borrador');
      if (borrRaw) {
        const borr = JSON.parse(borrRaw);
        if (borr?.resumir_paso === 2) {
          setPaso(2);
          // Consumir el flag para que si el user hace "Volver al formulario" y
          // modifica, luego al volver a generar PDF no reaparezca el salto.
          const cleaned = { ...borr };
          delete cleaned.resumir_paso;
          localStorage.setItem('aux_otro_distrito_borrador', JSON.stringify(cleaned));
          setBorradorInfo(cleaned);
        }
      }
    } catch { /* no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter188: Autoguardado local (debounced) en el mismo browser.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(
          'aux_otro_distrito_formdata',
          JSON.stringify({ formData, cursoEsteAnio }),
        );
      } catch { /* no-op (quota) */ }
    }, 400);
    return () => clearTimeout(t);
  }, [formData, cursoEsteAnio]);

  const deptos = formData.departamentos_judiciales || [];
  const legales = formData.domicilios_legales || {};

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDepartamentosChange = (nextDeptos) => {
    setFormData((prev) => {
      const nextLegales = { ...(prev.domicilios_legales || {}) };
      // Agregar faltantes
      nextDeptos.forEach((d) => {
        if (!nextLegales[d]) nextLegales[d] = { ...DOM_LEGAL_VACIO };
      });
      // Quitar los que ya no están
      Object.keys(nextLegales).forEach((k) => {
        if (!nextDeptos.includes(k)) delete nextLegales[k];
      });
      return { ...prev, departamentos_judiciales: nextDeptos, domicilios_legales: nextLegales };
    });
  };

  const handleLegalCampo = (dep, field, value) => {
    setFormData((prev) => ({
      ...prev,
      domicilios_legales: {
        ...(prev.domicilios_legales || {}),
        [dep]: {
          ...(prev.domicilios_legales?.[dep] || DOM_LEGAL_VACIO),
          [field]: value,
        },
      },
    }));
  };

  // Validaciones del paso 1
  const camposFaltantesPaso1 = () => {
    const faltantes = [];
    if (!formData.tipo_inscripcion) faltantes.push('Tipo de inscripción');
    if (!formData.apellido) faltantes.push('Apellido');
    if (!formData.nombres) faltantes.push('Nombres');
    if (!formData.documento) faltantes.push('DNI');
    if (!formData.domicilio_profesional) faltantes.push('Domicilio profesional');
    if (!formData.localidad_profesional) faltantes.push('Localidad profesional');
    if (!formData.cp_profesional) faltantes.push('CP profesional');
    if (!formData.tel_profesional) faltantes.push('Teléfono profesional');
    if (!formData.domicilio_real) faltantes.push('Domicilio real');
    if (!formData.tel_real_codigo_area || !formData.tel_real_numero)
      faltantes.push('Teléfono real (cód. área y número)');
    if (!formData.email_personal) faltantes.push('Email personal');
    if (!formData.titulo_habilitante) faltantes.push('Título habilitante');
    if (!formData.fecha_graduacion) faltantes.push('Fecha de graduación');
    if (!formData.fuero_civil && !formData.fuero_laboral) faltantes.push('Al menos un fuero (Civil o Laboral)');
    if (!formData.domicilio_electronico_corte) faltantes.push('Domicilio electrónico (Corte)');
    if (formData.tipo_inscripcion === 'primera_vez' && !formData.lugar_fecha_curso_procesal)
      faltantes.push('Lugar y fecha del curso procesal');
    if (deptos.length === 0) faltantes.push('Departamentos judiciales (al menos uno)');
    deptos.forEach((d) => {
      const dl = legales[d] || {};
      if (!dl.domicilio) faltantes.push(`Dirección Legal (${d})`);
      if (!dl.localidad) faltantes.push(`Localidad Legal (${d})`);
      if (!dl.cp) faltantes.push(`CP Legal (${d})`);
      if (!dl.tel_codigo_area || !dl.tel_numero)
        faltantes.push(`Teléfono Legal (${d}) — cód. área y número`);
    });
    if (!formData.documentacion_acompanada) faltantes.push('Documentación acompañada');
    return faltantes;
  };

  // Construye el payload de datos (compone tel_real y tel legal por depto)
  const buildPayload = () => {
    const ca = (formData.tel_real_codigo_area || '').trim();
    const num = (formData.tel_real_numero || '').trim();
    const telReal = ca && num ? `${ca}-${num}` : '';
    // Componer tel por depto (cod. área + número → "ca-num") para que el
    // backend pueda normalizarlo y disparar WhatsApp/Ayni.
    const legalesCompuestos = {};
    Object.entries(formData.domicilios_legales || {}).forEach(([dep, dl]) => {
      const dlCa = (dl?.tel_codigo_area || '').trim();
      const dlNum = (dl?.tel_numero || '').trim();
      legalesCompuestos[dep] = {
        domicilio: dl?.domicilio || '',
        cp: dl?.cp || '',
        localidad: dl?.localidad || '',
        tel: dlCa && dlNum ? `${dlCa}-${dlNum}` : '',
        tel_codigo_area: dlCa,
        tel_numero: dlNum,
      };
    });
    return {
      ...formData,
      domicilios_legales: legalesCompuestos,
      tel_real: telReal,
      curso_este_anio: cursoEsteAnio,
    };
  };

  const handleGenerarPDF = async () => {
    setError(null);
    const faltantes = camposFaltantesPaso1();
    if (faltantes.length) {
      setCamposFaltantesModal({ open: true, campos: faltantes });
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();

      // iter188: Primero guardar borrador público + enviar email con link + código.
      // iter194: En modo corrección NO guardamos borrador (ya hay solicitud en DB).
      if (!esCorreccion) {
        try {
          const borrResp = await axios.post(
            `${API_URL}/auxiliares-justicia/publico/borrador`,
            {
              datos: payload,
              distrito_origen: distritoOrigen,
              email_contacto: payload.email_personal,
              curso_este_anio: cursoEsteAnio,
              token: borradorInfo?.token || null,
            },
          );
          const info = {
            token: borrResp.data.token,
            codigo: borrResp.data.codigo,
            link: borrResp.data.link,
            email_enviado: borrResp.data.email_enviado,
            email_contacto: payload.email_personal,
          };
          setBorradorInfo(info);
          try {
            localStorage.setItem('aux_otro_distrito_borrador', JSON.stringify(info));
          } catch { /* no-op */ }
        } catch (borrErr) {
          console.warn('No se pudo guardar borrador público:', borrErr);
        }
      }

      // Generar PDF para firmar
      const res = await axios.post(
        `${API_URL}/auxiliares-justicia/publico/generar-pdfs-otros-distritos`,
        { datos: payload, distrito_origen: distritoOrigen },
        { responseType: 'blob' },
      );
      const blob = res.data;
      const contentType = res.headers['content-type'] || 'application/pdf';
      const isPdf = contentType.includes('pdf');
      const ext = isPdf ? 'pdf' : 'zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `formulario_auxiliar_${(formData.apellido || 'solicitud').toLowerCase()}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      setPaso(2);
    } catch (err) {
      let detail = 'No pudimos generar el PDF.';
      try {
        const txt = await err.response?.data?.text?.();
        if (txt) detail = JSON.parse(txt).detail || detail;
      } catch (_) { /* no-op */ }
      setError(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDescargarPDFNuevamente = async () => {
    // Mismo endpoint, mismo payload — útil cuando el usuario perdió el archivo.
    try {
      const payload = buildPayload();
      const res = await axios.post(
        `${API_URL}/auxiliares-justicia/publico/generar-pdfs-otros-distritos`,
        { datos: payload, distrito_origen: distritoOrigen },
        { responseType: 'blob' },
      );
      const blob = res.data;
      const contentType = res.headers['content-type'] || 'application/pdf';
      const ext = contentType.includes('pdf') ? 'pdf' : 'zip';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `formulario_auxiliar_${(formData.apellido || 'solicitud').toLowerCase()}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) { /* no-op */ }
  };

  const handleEmpezarDeNuevo = () => {
    if (!window.confirm('¿Seguro que querés empezar de nuevo? Vas a perder los datos cargados.')) return;
    setFormData(INITIAL_FORMDATA);
    setCursoEsteAnio(false);
    setFirmadosPorDep({});
    setCertificado(null);
    setCopiaDni(null);
    setSinCertificadoAun(false);
    setBorradorInfo(null);
    try {
      localStorage.removeItem('aux_otro_distrito_formdata');
      localStorage.removeItem('aux_otro_distrito_borrador');
    } catch { /* no-op */ }
    setPaso(1);
  };

  const handleEnviarInscripcion = async () => {
    setError(null);
    const faltantes = [];
    deptos.forEach((d) => {
      if (!firmadosPorDep[d]) faltantes.push(`Formulario firmado — ${d}`);
    });
    if (!copiaDni) faltantes.push('Copia de DNI');
    const esRenov = formData.tipo_inscripcion === 'renovacion';
    if (!esRenov && !sinCertificadoAun && !certificado) {
      faltantes.push('Certificado/Acta del curso procesal');
    }
    if (faltantes.length) {
      setCamposFaltantesModal({ open: true, campos: faltantes });
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const fd = new FormData();
      fd.append('datos_json', JSON.stringify(payload));
      fd.append('distrito_origen', distritoOrigen);
      deptos.forEach((d) => {
        fd.append('formularios_firmados', firmadosPorDep[d], firmadosPorDep[d].name);
      });
      fd.append('formularios_firmados_deptos', JSON.stringify(deptos));
      if (certificado) fd.append('certificado', certificado, certificado.name);
      fd.append('copia_dni', copiaDni, copiaDni.name);
      fd.append('sin_certificado_aun', String(sinCertificadoAun));
      // iter202: enviamos el token del borrador para que el backend lo marque
      // como "enviado" y el frontend NO lo lea como pendiente al recargar.
      if (borradorInfo?.token) {
        fd.append('token_borrador', borradorInfo.token);
      }

      let res;
      try {
        res = await axios.post(
          esCorreccion
            ? `${API_URL}/auxiliares-justicia/publico/correccion/${correccionToken}/enviar`
            : `${API_URL}/auxiliares-justicia/publico/enviar-otros-distritos`,
          fd,
          { timeout: 90000 },
        );
      } catch (networkErr) {
        // iter202 · Timeout o error de red:
        // ANTES de mostrar error, reconsultar estado por token (si tenemos uno
        // persistido en localStorage de un envío previo). Si el backend ya
        // tiene la solicitud en estado "enviada", tratamos como éxito —
        // probablemente el mail en background se demoró y el proxy cortó.
        const isTimeout = networkErr.code === 'ECONNABORTED' || /timeout/i.test(networkErr.message || '');
        const isNetwork = !networkErr.response;
        if (isTimeout || isNetwork) {
          // Buscar token_correccion en localStorage o en una respuesta parcial.
          let tokenParaChequear = null;
          try {
            const prev = JSON.parse(localStorage.getItem('aux_otro_distrito_last_response') || 'null');
            tokenParaChequear = prev?.token_correccion || null;
          } catch { /* no-op */ }
          if (tokenParaChequear) {
            try {
              const chk = await axios.get(
                `${API_URL}/auxiliares-justicia/publico/estado-por-token/${tokenParaChequear}`,
                { timeout: 15000 },
              );
              if (chk.data?.estado === 'enviada') {
                res = { data: { token_correccion: tokenParaChequear, solicitud_id: chk.data.solicitud_id } };
              }
            } catch { /* no-op → re-tiramos el error original */ }
          }
          if (!res) throw networkErr;
        } else {
          throw networkErr;
        }
      }
      setTokenCorreccion(res.data.token_correccion || '');
      // iter202 · Persistimos el token para que un reintento manual pueda
      // detectar que la solicitud ya fue creada.
      try {
        localStorage.setItem(
          'aux_otro_distrito_last_response',
          JSON.stringify({ token_correccion: res.data.token_correccion, at: Date.now() }),
        );
      } catch { /* no-op */ }
      sessionStorage.removeItem('aux_otro_distrito_origen');
      // iter188/194: limpiar borrador público + localStorage al enviar OK.
      try {
        localStorage.removeItem('aux_otro_distrito_formdata');
        localStorage.removeItem('aux_otro_distrito_borrador');
        localStorage.removeItem('aux_otro_distrito_correccion');
      } catch { /* no-op */ }
      setSuccess(true);
    } catch (err) {
      const detail =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Error al enviar la inscripción. Reintentá.';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Encabezado + chip de distrito de origen ───
  const Encabezado = () => (
    <div className="mb-6">
      <div className="flex justify-center mb-4">
        <img src="/logo-colegio.png" alt="Logo del Colegio" className="h-20 w-auto object-contain" />
      </div>
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Scale className="w-10 h-10 text-amber-600" />
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Auxiliares de Justicia</h1>
        </div>
        <p className="text-gray-600 text-sm">Inscripción al Listado Oficial según Acordada 2728</p>
      </div>
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="pt-4 pb-4 text-center">
          <p className="text-xs text-amber-900">
            Flujo público para matriculadxs de otro distrito · Distrito de origen:{' '}
            <strong data-testid="distrito-origen-label">{distritoOrigen}</strong>
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // ─── Pantalla de éxito ───
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-10 px-4">
        <div className="max-w-2xl mx-auto">
          <Encabezado />
          <Card className="border-green-200" data-testid="paso-confirmacion">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2 text-green-700">¡Inscripción enviada!</h2>
              <p className="text-gray-600 mb-4">
                Tu solicitud fue registrada correctamente. Recibirás un email de confirmación en breve.
                El Colegio revisará tu documentación y te contactará por este medio con la resolución.
              </p>
              {tokenCorreccion && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-left text-xs text-gray-700 mb-4">
                  <p className="font-semibold mb-1">⚠️ Enlace para correcciones</p>
                  <p className="mb-2">
                    Si tu solicitud recibe una observación, podrás corregirla desde este enlace
                    (también lo recibís por email):
                  </p>
                  <code className="block bg-white border px-2 py-1 rounded break-all">
                    {`${window.location.origin}/auxiliares-justicia/corregir/${tokenCorreccion}`}
                  </code>
                </div>
              )}
              <Link to="/">
                <Button variant="outline">Volver al inicio</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-4xl mx-auto">
        <Encabezado />

        {/* Stepper */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${paso >= 1 ? 'text-amber-600' : 'text-gray-400'}`}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                paso >= 1 ? 'bg-amber-600 text-white' : 'bg-gray-200'
              }`}
            >
              {paso > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
            </div>
            <span className="hidden sm:inline font-medium">Completar formulario</span>
          </div>
          <div className="w-12 h-0.5 bg-gray-200" />
          <div className={`flex items-center gap-2 ${paso >= 2 ? 'text-amber-600' : 'text-gray-400'}`}>
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${
                paso >= 2 ? 'bg-amber-600 text-white' : 'bg-gray-200'
              }`}
            >
              2
            </div>
            <span className="hidden sm:inline font-medium">Subir documentación</span>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="alerta-error">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        {/* iter194: Banner de modo corrección */}
        {esCorreccion && (
          <div
            className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg"
            data-testid="banner-modo-correccion"
          >
            <p className="font-semibold text-amber-900 mb-1 flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Estás corrigiendo una solicitud observada
            </p>
            <p className="text-sm text-amber-900">
              Revisá y actualizá los datos del formulario según el motivo de la observación.
              Luego vas a tener que descargar los PDFs nuevos y volver a firmarlos.
            </p>
            {correccionMotivo && (
              <div className="mt-2 bg-white border border-amber-200 rounded p-2 text-sm text-amber-950">
                <strong>Motivo de la observación:</strong>
                <p className="whitespace-pre-wrap mt-1">{correccionMotivo}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══════════════════ PASO 1: Formulario ═══════════════════ */}
        {paso === 1 && (
          <Card data-testid="paso-datos-formulario">
            <CardHeader>
              <CardTitle>Paso 1: Completá el formulario</CardTitle>
              <CardDescription>
                Ingresá tus datos. Luego podrás descargar el PDF para firmarlo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* ── Preguntas clave ── */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-amber-800">Información importante</h3>

                {/* Tipo de inscripción */}
                <div>
                  <Label className="mb-2 block">Tipo de Inscripción *</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="select-tipo-inscripcion">
                    {[
                      { value: 'primera_vez', label: 'Inscripción por primera vez', testId: 'opt-primera-vez' },
                      { value: 'renovacion', label: 'Renovación anual (Re-inscripción)', testId: 'opt-renovacion' },
                    ].map((opt) => {
                      const selected = formData.tipo_inscripcion === opt.value;
                      return (
                        <label
                          key={opt.value}
                          className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors bg-white ${
                            selected
                              ? 'border-amber-500 ring-1 ring-amber-500'
                              : 'border-amber-200 hover:border-amber-400'
                          }`}
                          data-testid={opt.testId}
                        >
                          <input
                            type="radio"
                            name="tipo_inscripcion"
                            value={opt.value}
                            checked={selected}
                            onChange={() => handleInputChange('tipo_inscripcion', opt.value)}
                            className="w-4 h-4 text-amber-600 flex-shrink-0"
                          />
                          <span className={`text-sm ${selected ? 'font-semibold text-amber-900' : 'text-gray-800'}`}>
                            {opt.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                  {formData.tipo_inscripcion === 'renovacion' && (
                    <div
                      className="mt-3 bg-white border-l-4 border-amber-500 rounded p-3 flex gap-2 items-start"
                      data-testid="aviso-renovacion"
                    >
                      <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-900 leading-relaxed">
                        Al renovar tu inscripción, ratificás que mantenés tu{' '}
                        <strong>Firma Digital vigente</strong> y tu <strong>matrícula al día</strong>{' '}
                        conforme a la normativa de la SCBA.
                      </p>
                    </div>
                  )}
                </div>

                {/* Curso de capacitación */}
                <div className="space-y-2">
                  <Label>¿Realizaste el curso de capacitación procesal este año?</Label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={cursoEsteAnio}
                        onChange={() => setCursoEsteAnio(true)}
                        className="w-4 h-4 text-amber-600"
                        data-testid="radio-curso-si"
                      />
                      <span>Sí, este año</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!cursoEsteAnio}
                        onChange={() => setCursoEsteAnio(false)}
                        className="w-4 h-4 text-amber-600"
                        data-testid="radio-curso-no"
                      />
                      <span>No, en años anteriores</span>
                    </label>
                  </div>
                  {cursoEsteAnio && (
                    <p className="text-sm text-amber-700 mt-1">
                      <Info className="w-4 h-4 inline mr-1" />
                      Si aún no te emitieron el certificado, podrás indicarlo en el paso 2 y enviar solo el formulario firmado.
                    </p>
                  )}
                </div>

                {/* Aviso específico: siempre deben adjuntar DNI (otros distritos) */}
                <div className="bg-white border-l-4 border-amber-500 rounded p-3 flex gap-2 items-start">
                  <Info className="w-4 h-4 text-amber-700 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 leading-relaxed">
                    Como sos matriculadx de otro distrito (<strong>{distritoOrigen}</strong>),
                    en el paso 2 deberás adjuntar <strong>copia de DNI</strong> junto con el formulario firmado.
                  </p>
                </div>
              </div>

              {/* Datos personales */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Apellido *</Label>
                  <Input
                    value={formData.apellido}
                    onChange={(e) => handleInputChange('apellido', e.target.value)}
                    data-testid="input-apellido"
                  />
                </div>
                <div>
                  <Label>Nombres *</Label>
                  <Input
                    value={formData.nombres}
                    onChange={(e) => handleInputChange('nombres', e.target.value)}
                    data-testid="input-nombres"
                  />
                </div>
                <div>
                  <Label>Nacionalidad</Label>
                  <Input
                    value={formData.nacionalidad}
                    onChange={(e) => handleInputChange('nacionalidad', e.target.value)}
                    data-testid="input-nacionalidad"
                  />
                </div>
                <div>
                  <Label>DNI *</Label>
                  <Input
                    value={formData.documento}
                    onChange={(e) => handleInputChange('documento', e.target.value)}
                    data-testid="input-documento"
                  />
                </div>
              </div>

              {/* Aclaración general sobre domicilios */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-testid="aviso-domicilios">
                <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-900 leading-relaxed space-y-1.5">
                  <p>
                    <strong>Regla clave:</strong> el domicilio <strong>Legal</strong> debe respetar
                    la jurisdicción del departamento judicial en el que te inscribís (Resolución SCBA).
                    Los tres domicilios (<strong>Real</strong>, <strong>Profesional</strong> y{' '}
                    <strong>Legal</strong>) pueden coincidir entre sí siempre que el Legal cumpla
                    con la jurisdicción exigida:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>
                      <strong>Lomas de Zamora:</strong> el domicilio legal debe estar dentro del{' '}
                      <button
                        type="button"
                        onClick={() => setMapaLomasOpen(true)}
                        className="inline items-baseline font-semibold text-amber-800 underline decoration-dotted underline-offset-2 hover:text-amber-950 hover:decoration-solid"
                        data-testid="link-radio-departamental"
                      >
                        radio de Tribunales (ver mapa)
                      </button>.
                    </li>
                    <li>
                      <strong>Avellaneda-Lanús:</strong> el domicilio legal debe estar dentro de los partidos de
                      <strong> Avellaneda</strong> o <strong>Lanús</strong>.
                    </li>
                  </ul>
                  <p className="text-xs pt-1">
                    Si te inscribís en los dos departamentos, cada formulario lleva su propio domicilio legal cumpliendo el criterio de esa jurisdicción.
                  </p>
                </div>
              </div>

              {/* Domicilio Profesional */}
              <div>
                <h3 className="font-semibold mb-1">Domicilio Profesional *</h3>
                <details className="mb-3 group" data-testid="info-domicilio-profesional">
                  <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Más información</span>
                    <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                    <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                    Es el domicilio desde el cual ejercés tu profesión: sede, oficina o consultorio.
                    Debe estar ubicado en alguno de los seis municipios del CATSLZ
                    (Lomas de Zamora, Almirante Brown, Esteban Echeverría, Ezeiza, Avellaneda o Lanús).
                    No es obligatorio consignar el domicilio de tu empleador.
                    Podés poner tu domicilio real (si está dentro de alguno de los municipios mencionados anteriormente).
                    <br /><br />
                    <strong>Coincidencias:</strong> puede coincidir únicamente con el domicilio <strong>Real</strong>.
                    No puede coincidir con el <strong>Legal</strong>, salvo que este último cumpla con el
                    radio departamental que exige la SCBA.
                  </p>
                </details>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Dirección *</Label>
                    <Input
                      value={formData.domicilio_profesional}
                      onChange={(e) => handleInputChange('domicilio_profesional', e.target.value)}
                      placeholder="Calle y número"
                      data-testid="input-domicilio-profesional"
                    />
                  </div>
                  <div>
                    <Label>Localidad *</Label>
                    <Input
                      value={formData.localidad_profesional}
                      onChange={(e) => handleInputChange('localidad_profesional', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>CP *</Label>
                    <Input
                      value={formData.cp_profesional}
                      onChange={(e) => handleInputChange('cp_profesional', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Teléfono *</Label>
                    <Input
                      value={formData.tel_profesional}
                      onChange={(e) => handleInputChange('tel_profesional', e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Domicilio Real */}
              <div>
                <h3 className="font-semibold mb-1">Domicilio Real *</h3>
                <details className="mb-3 group" data-testid="info-domicilio-real">
                  <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                    <Info className="w-4 h-4 flex-shrink-0" />
                    <span>Más información</span>
                    <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                    <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                  </summary>
                  <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                    Es tu domicilio particular, donde vivís efectivamente. Puede estar fuera de los
                    municipios del CATSLZ y de los departamentos judiciales en los que solicitás inscripción.
                    <br /><br />
                    <strong>Coincidencias:</strong> puede coincidir únicamente con el domicilio <strong>Profesional</strong>.
                    No puede coincidir con el <strong>Legal</strong>, salvo que este último cumpla con el
                    radio departamental que exige la SCBA.
                  </p>
                </details>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label>Dirección *</Label>
                    <Input
                      value={formData.domicilio_real}
                      onChange={(e) => handleInputChange('domicilio_real', e.target.value)}
                      placeholder="Calle y número"
                      data-testid="input-domicilio-real"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Teléfono / Celular *</Label>
                    <div className="grid grid-cols-[1fr_2fr] gap-2">
                      <div>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={4}
                          value={formData.tel_real_codigo_area}
                          onChange={(e) =>
                            handleInputChange('tel_real_codigo_area', e.target.value.replace(/\D/g, '').slice(0, 4))
                          }
                          placeholder="Cód. Área (sin 0)"
                          data-testid="input-tel-real-codigo-area"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Ej: 11, 221, 2352</p>
                      </div>
                      <div>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={9}
                          value={formData.tel_real_numero}
                          onChange={(e) =>
                            handleInputChange('tel_real_numero', e.target.value.replace(/\D/g, '').slice(0, 9))
                          }
                          placeholder="Número (sin 15)"
                          data-testid="input-tel-real-numero"
                        />
                        <p className="text-[11px] text-gray-500 mt-1">Sólo dígitos, sin espacios ni guiones</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div>
                <Label>Email Personal *</Label>
                <Input
                  type="email"
                  value={formData.email_personal}
                  onChange={(e) => handleInputChange('email_personal', e.target.value)}
                  data-testid="input-email-personal"
                />
              </div>

              {/* Formación */}
              <div>
                <h3 className="font-semibold mb-3">Formación</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Título Habilitante *</Label>
                    <Select
                      value={formData.titulo_habilitante}
                      onValueChange={(v) => handleInputChange('titulo_habilitante', v)}
                    >
                      <SelectTrigger data-testid="select-titulo">
                        <SelectValue placeholder="Seleccionar título" />
                      </SelectTrigger>
                      <SelectContent>
                        {TITULOS_HABILITANTES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fecha de Graduación *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="btn-fecha-graduacion">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.fecha_graduacion ? (
                            format(parse(formData.fecha_graduacion, 'yyyy-MM-dd', new Date()), 'dd/MM/yyyy')
                          ) : (
                            <span className="text-muted-foreground">Seleccionar fecha</span>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            formData.fecha_graduacion
                              ? parse(formData.fecha_graduacion, 'yyyy-MM-dd', new Date())
                              : undefined
                          }
                          onSelect={(date) =>
                            handleInputChange('fecha_graduacion', date ? format(date, 'yyyy-MM-dd') : '')
                          }
                          initialFocus
                          captionLayout="dropdown-buttons"
                          fromYear={1960}
                          toYear={new Date().getFullYear()}
                          classNames={{
                            caption_dropdowns: 'flex gap-2',
                            dropdown: 'bg-white border rounded px-2 py-1 text-sm',
                            dropdown_month: 'bg-white',
                            dropdown_year: 'bg-white',
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Inscripción: fueros + depto electrónico + curso */}
              <div>
                <h3 className="font-semibold mb-3">Inscripción</h3>
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.fuero_civil}
                        onCheckedChange={(v) => handleInputChange('fuero_civil', v)}
                        data-testid="chk-fuero-civil"
                      />
                      <span>Fuero Civil *</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.fuero_laboral}
                        onCheckedChange={(v) => handleInputChange('fuero_laboral', v)}
                        data-testid="chk-fuero-laboral"
                      />
                      <span>Fuero Laboral *</span>
                    </label>
                  </div>
                  <div>
                    <Label>Domicilio Electrónico (otorgado por la Corte) *</Label>
                    <Input
                      value={formData.domicilio_electronico_corte}
                      onChange={(e) => handleInputChange('domicilio_electronico_corte', e.target.value)}
                      placeholder="Ej: 12345678@cts.notificaciones"
                      data-testid="input-domicilio-electronico"
                    />
                  </div>
                  {formData.tipo_inscripcion === 'primera_vez' && (
                    <div data-testid="campo-curso-procesal">
                      <Label>Lugar y Fecha del Curso de Capacitación Procesal *</Label>
                      <Input
                        value={formData.lugar_fecha_curso_procesal}
                        onChange={(e) => handleInputChange('lugar_fecha_curso_procesal', e.target.value)}
                      />
                    </div>
                  )}

                  {/* ── Departamentos judiciales (multiselect hasta 2) ── */}
                  <div>
                    <Label>
                      Departamentos Judiciales donde solicitás inscripción *{' '}
                      <span className="text-xs font-normal text-gray-500">(podés elegir uno o los dos)</span>
                    </Label>
                    <SelectorDepartamentos
                      value={deptos}
                      onChange={handleDepartamentosChange}
                    />
                    <p className="text-xs text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded-md p-2 leading-relaxed">
                      <strong>Importante:</strong> este Colegio Distrital (CATSLZ) sólo habilita la inscripción
                      en los departamentos judiciales de <strong>Lomas de Zamora</strong> y/o
                      <strong> Avellaneda-Lanús</strong>. Podés elegir uno o ambos: si elegís los dos,
                      el sistema generará un formulario por cada departamento, compartiendo la misma
                      documentación adjunta.
                    </p>
                  </div>

                  {/* ── Domicilio Legal por departamento (dinámico) ── */}
                  {deptos.length > 0 && (
                    <div className="border-l-4 border-amber-500 pl-4 space-y-6" data-testid="bloque-domicilios-legales">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {deptos.length === 1 ? 'Domicilio Legal *' : 'Domicilios Legales por Departamento *'}
                        </h3>
                        <details className="mt-1 group" data-testid="info-domicilio-legal">
                          <summary className="cursor-pointer select-none text-sm font-medium text-amber-700 hover:text-amber-800 inline-flex items-center gap-1.5 list-none">
                            <Info className="w-4 h-4 flex-shrink-0" />
                            <span>Más información</span>
                            <span className="text-xs text-amber-600 group-open:hidden">(mostrar)</span>
                            <span className="text-xs text-amber-600 hidden group-open:inline">(ocultar)</span>
                          </summary>
                          <p className="mt-2 text-sm text-gray-700 leading-relaxed bg-amber-50 border border-amber-200 rounded-md p-3">
                            El <strong>Domicilio Legal</strong> es donde el/la auxiliar recibe las notificaciones
                            en su carácter de perito. Por <strong>Resolución de la SCBA</strong> debe
                            constituirse <strong>dentro del radio del departamento judicial</strong> en el que
                            solicitás la inscripción.
                            {deptos.length > 1 && (
                              <> Como elegiste {deptos.length} departamentos, debés declarar un domicilio legal por cada uno.</>
                            )}
                            <br /><br />
                            <strong>Coincidencias:</strong> solo el domicilio <strong>Real</strong> y el
                            <strong> Profesional</strong> pueden coincidir entre sí. El Legal solo podrá
                            coincidir con ellos si cumple con el radio departamental exigido.
                          </p>
                        </details>
                      </div>

                      {deptos.map((dep) => {
                        const dl = legales[dep] || DOM_LEGAL_VACIO;
                        return (
                          <div
                            key={dep}
                            className="bg-amber-50/40 border border-amber-200 rounded-lg p-4 space-y-3"
                            data-testid={`legal-block-${dep}`}
                          >
                            <div className="flex items-center gap-2 flex-wrap">
                              <Scale className="w-4 h-4 text-amber-700" />
                              <p className="font-medium text-amber-900">
                                Domicilio Legal para{' '}
                                <span className="underline decoration-amber-400 underline-offset-2">{dep}</span>
                              </p>
                              {dep === DEPTO_LOMAS && (
                                <button
                                  type="button"
                                  onClick={() => setMapaLomasOpen(true)}
                                  className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2"
                                  data-testid="btn-abrir-mapa-lomas"
                                >
                                  <MapPin className="w-3.5 h-3.5" />
                                  Ver radio judicial (mapa)
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div className="md:col-span-2">
                                <Label className="text-xs">Dirección *</Label>
                                <Input
                                  value={dl.domicilio}
                                  onChange={(e) => handleLegalCampo(dep, 'domicilio', e.target.value)}
                                  placeholder="Calle y número"
                                  data-testid={`input-legal-domicilio-${dep}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Localidad *</Label>
                                <Input
                                  value={dl.localidad}
                                  onChange={(e) => handleLegalCampo(dep, 'localidad', e.target.value)}
                                  data-testid={`input-legal-localidad-${dep}`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs">CP *</Label>
                                <Input
                                  value={dl.cp}
                                  onChange={(e) => handleLegalCampo(dep, 'cp', e.target.value)}
                                  data-testid={`input-legal-cp-${dep}`}
                                />
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-xs">Teléfono *</Label>
                                <p className="text-[11px] text-gray-500 mb-1">
                                  Para que el Colegio pueda avisarte novedades por WhatsApp / Ayni.
                                </p>
                                <div className="grid grid-cols-[1fr_2fr] gap-2">
                                  <div>
                                    <Input
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      maxLength={4}
                                      value={dl.tel_codigo_area || ''}
                                      onChange={(e) =>
                                        handleLegalCampo(
                                          dep,
                                          'tel_codigo_area',
                                          e.target.value.replace(/\D/g, '').slice(0, 4),
                                        )
                                      }
                                      placeholder="Cód. Área"
                                      data-testid={`input-legal-tel-ca-${dep}`}
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Sin 0. Ej: 11, 221</p>
                                  </div>
                                  <div>
                                    <Input
                                      type="tel"
                                      inputMode="numeric"
                                      pattern="[0-9]*"
                                      maxLength={9}
                                      value={dl.tel_numero || ''}
                                      onChange={(e) =>
                                        handleLegalCampo(
                                          dep,
                                          'tel_numero',
                                          e.target.value.replace(/\D/g, '').slice(0, 9),
                                        )
                                      }
                                      placeholder="Número (sin 15)"
                                      data-testid={`input-legal-tel-num-${dep}`}
                                    />
                                    <p className="text-[10px] text-gray-500 mt-1">Sólo dígitos</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            {dep === DEPTO_LOMAS && (
                              <p
                                className="text-[11px] text-red-600 leading-snug pt-1"
                                data-testid="aviso-radio-lomas"
                              >
                                <strong>Recordá</strong> que para este depto judicial, el domicilio legal debe estar dentro del radio de Tribunales (
                                <button
                                  type="button"
                                  onClick={() => setMapaLomasOpen(true)}
                                  className="underline hover:no-underline font-semibold"
                                >
                                  ver mapa
                                </button>
                                ).
                              </p>
                            )}
                            {dep === 'Avellaneda-Lanús' && (
                              <p
                                className="text-[11px] text-red-600 leading-snug pt-1"
                                data-testid="aviso-avellaneda-lanus"
                              >
                                <strong>Recordá</strong> que para este depto judicial, el domicilio legal debe estar dentro de los partidos de <strong>Avellaneda</strong> o <strong>Lanús</strong>.
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div>
                    <Label>Documentación acompañada *</Label>
                    <textarea
                      className="w-full min-h-[80px] p-3 border rounded-md text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-500"
                      value={formData.documentacion_acompanada}
                      onChange={(e) => handleInputChange('documentacion_acompanada', e.target.value)}
                      placeholder="Detallá la documentación que adjuntás..."
                      data-testid="input-documentacion-acompanada"
                    />
                  </div>
                </div>
              </div>

              {/* Botón generar PDF */}
              <div className="flex flex-col sm:flex-row sm:justify-end gap-2 pt-4 border-t">
                <Link to="/auxiliares-justicia/otros-distritos">
                  <Button variant="outline" data-testid="btn-cambiar-distrito">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Cambiar distrito
                  </Button>
                </Link>
                <Button
                  onClick={handleGenerarPDF}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-generar-pdf"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generando...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" /> Generar y Descargar{' '}
                      {deptos.length > 1 ? `${deptos.length} PDFs` : 'PDF'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ═══════════════════ PASO 2: Subir documentación ═══════════════════ */}
        {paso === 2 && (
          <Card data-testid="paso-subir-firmados">
            <CardHeader>
              <CardTitle>Paso 2: Subir documentación firmada</CardTitle>
              <CardDescription>
                {deptos.length > 1 ? (
                  <>
                    Se generó un formulario por cada departamento judicial elegido. Imprimilos, firmalos
                    de puño y letra, y <strong>subí cada uno por separado</strong> en su casillero.
                    No hace falta que los combines en un único PDF.
                  </>
                ) : (
                  <>
                    Imprimí el PDF, firmalo de puño y letra, y subilo junto con la documentación requerida.
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* iter188: banner con link + código del borrador para retomar
                  desde otro dispositivo. */}
              {borradorInfo && (
                <div
                  className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                  data-testid="borrador-info-banner"
                >
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm text-blue-900">
                      <p className="font-semibold mb-1">Tu borrador está guardado</p>
                      <p className="mb-2">
                        {borradorInfo.email_enviado
                          ? <>Te enviamos un email a <strong>{borradorInfo.email_contacto}</strong> con un link para retomar la inscripción desde cualquier dispositivo. Si no te llega, revisá spam.</>
                          : <>Guardamos tu borrador en nuestros servidores. (No pudimos enviarte el email a <strong>{borradorInfo.email_contacto}</strong>, pero podés usar el código de abajo para retomar.)</>
                        }
                      </p>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white rounded p-2 border border-blue-200">
                        <span className="text-xs text-gray-600">Tu código de rescate:</span>
                        <code
                          className="font-mono font-bold text-base text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200 tracking-wider"
                          data-testid="borrador-codigo"
                        >
                          {borradorInfo.codigo}
                        </code>
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(borradorInfo.codigo).catch(() => {});
                          }}
                          className="text-xs text-blue-700 underline hover:no-underline"
                          data-testid="borrador-copiar-codigo"
                        >
                          Copiar
                        </button>
                        <span className="text-xs text-gray-500 sm:ml-auto">
                          Guardalo por si perdés el mail
                        </span>
                      </div>
                      <p className="text-[11px] text-blue-800 mt-2">
                        Para retomar: entrá a{' '}
                        <code className="bg-white px-1 rounded border">
                          /auxiliares-justicia/otros-distritos/retomar
                        </code>{' '}
                        y pegá el código.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Resumen */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold mb-2">Resumen de tu inscripción</h3>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Nombre:</strong> {formData.apellido}, {formData.nombres}</p>
                  <p><strong>DNI:</strong> {formData.documento}</p>
                  <p><strong>Distrito de origen:</strong> {distritoOrigen}</p>
                  <p><strong>Curso:</strong> {cursoEsteAnio ? 'Realizado este año' : 'Años anteriores'}</p>
                  <p><strong>Departamento(s) Judicial(es):</strong> {deptos.join(' · ') || '—'}</p>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDescargarPDFNuevamente}
                    data-testid="btn-descargar-pdf-nuevamente"
                    className="w-full sm:w-auto justify-center"
                  >
                    <Download className="w-4 h-4 mr-1" /> Descargar{' '}
                    {deptos.length > 1 ? 'ZIP con PDFs' : 'PDF'} nuevamente
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleEmpezarDeNuevo}
                    className="w-full sm:w-auto justify-center"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> Empezar de nuevo
                  </Button>
                </div>
              </div>

              {/* Documentación requerida */}
              <div className="space-y-4">
                <h3 className="font-semibold">Documentación requerida</h3>

                {/* Formularios firmados (uno por departamento) */}
                {deptos.map((dep) => {
                  const archivo = firmadosPorDep[dep];
                  const label =
                    deptos.length > 1 ? (
                      <>Formulario firmado — <span className="text-amber-700">{dep}</span> *</>
                    ) : (
                      <>Formulario firmado de puño y letra *</>
                    );
                  return (
                    <div
                      key={dep}
                      className="border rounded-lg p-4"
                      data-testid={`bloque-firmado-${dep}`}
                    >
                      <Label className="text-base font-medium block">{label}</Label>
                      <p className="text-sm text-gray-500 mb-2 mt-2">
                        Subí el PDF que descargaste, imprimiste y firmaste
                        {deptos.length > 1 && (
                          <> correspondiente a <strong>{dep}</strong></>
                        )}.
                      </p>
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                        onChange={(e) => {
                          const f = e.target.files[0];
                          setFirmadosPorDep((prev) => ({ ...prev, [dep]: f || undefined }));
                        }}
                        data-testid={`input-firmado-${dep}`}
                      />
                      {archivo && (
                        <p
                          className="text-sm text-green-600 mt-1"
                          data-testid={`filename-firmado-${dep}`}
                        >
                          ✓ {archivo.name}
                        </p>
                      )}
                    </div>
                  );
                })}

                {/* Certificado */}
                {(() => {
                  const esRenov = formData.tipo_inscripcion === 'renovacion';
                  return (
                    <div className="border rounded-lg p-4" data-testid="bloque-certificado">
                      <Label className="text-base font-medium">
                        Certificado o Acta del Curso de Capacitación Procesal
                        {!esRenov && !sinCertificadoAun && ' *'}
                        {esRenov && (
                          <span className="ml-2 text-xs font-normal text-gray-500">(opcional en Renovación)</span>
                        )}
                      </Label>

                      {esRenov && (
                        <p className="text-sm text-gray-600 mt-1" data-testid="cert-opcional-renovacion">
                          Como estás renovando, no es obligatorio adjuntar el certificado nuevamente.
                          Podés subirlo igual si querés dejarlo asentado.
                        </p>
                      )}

                      {!esRenov && cursoEsteAnio && (
                        <div className="my-3 p-3 bg-amber-50 rounded-lg">
                          <label className="flex items-start gap-2 cursor-pointer">
                            <Checkbox
                              checked={sinCertificadoAun}
                              onCheckedChange={(v) => {
                                setSinCertificadoAun(v);
                                if (v) setCertificado(null);
                              }}
                              data-testid="chk-sin-certificado"
                            />
                            <span className="text-sm text-amber-800">
                              Hice el curso este año y aún no me emitieron el certificado.
                              Me inscribo hasta tanto me lo envíen desde el Consejo Superior.
                            </span>
                          </label>
                        </div>
                      )}

                      {(esRenov || !sinCertificadoAun) && (
                        <>
                          <p className="text-sm text-gray-500 mb-2 mt-2">
                            {esRenov
                              ? 'Solo si querés subir uno actualizado (no requerido)'
                              : cursoEsteAnio
                                ? 'Podés subir el Acta de Aprobación si ya la tenés'
                                : 'Subí el certificado del curso'}
                          </p>
                          <Input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            onChange={(e) => setCertificado(e.target.files[0])}
                            data-testid="input-certificado"
                          />
                          {certificado && (
                            <p className="text-sm text-green-600 mt-1">✓ {certificado.name}</p>
                          )}
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Copia de DNI (SIEMPRE obligatorio para otros distritos) */}
                <div className="border rounded-lg p-4 border-amber-200 bg-amber-50/50" data-testid="bloque-dni">
                  <Label className="text-base font-medium">Copia de DNI *</Label>
                  <p className="text-sm text-gray-500 mb-2 mt-2">
                    Requerido para matriculadxs de otros distritos.
                  </p>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={(e) => setCopiaDni(e.target.files[0])}
                    data-testid="input-copia-dni"
                  />
                  {copiaDni && <p className="text-sm text-green-600 mt-1">✓ {copiaDni.name}</p>}
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" onClick={() => setPaso(1)} data-testid="btn-volver-formulario">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Volver al formulario
                </Button>
                <Button
                  onClick={handleEnviarInscripcion}
                  disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="btn-enviar-inscripcion"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Enviar Inscripción
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Modal: Campos faltantes */}
      <Dialog
        open={camposFaltantesModal.open}
        onOpenChange={(o) => setCamposFaltantesModal((prev) => ({ ...prev, open: o }))}
      >
        <DialogContent className="max-w-lg" data-testid="modal-campos-faltantes">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              Faltan datos obligatorios
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {paso === 2
                ? 'Antes de enviar la inscripción, adjuntá lo siguiente:'
                : 'Antes de generar los PDFs, completá los siguientes campos:'}
            </p>
            <div
              className="max-h-72 overflow-y-auto rounded-lg border border-red-200 bg-red-50 p-4"
              data-testid="lista-campos-faltantes"
            >
              <ul className="list-disc list-inside space-y-1 text-sm text-red-900">
                {camposFaltantesModal.campos.map((campo, idx) => (
                  <li key={idx} data-testid={`campo-faltante-${idx}`}>
                    {campo}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setCamposFaltantesModal({ open: false, campos: [] });
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="btn-cerrar-campos-faltantes"
              >
                Entendido
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Radio Judicial Lomas */}
      <Dialog open={mapaLomasOpen} onOpenChange={setMapaLomasOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="modal-radio-lomas">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-800">
              <MapPin className="w-5 h-5" />
              Radio Judicial Obligatorio — Lomas de Zamora
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg overflow-hidden border border-amber-200 bg-amber-50">
              <img
                src={LOMAS_MAPA_URL}
                alt="Mapa del radio judicial de Lomas de Zamora"
                className="w-full h-auto block"
                data-testid="img-mapa-lomas"
              />
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded p-4">
              <p className="text-sm font-semibold text-amber-900 mb-2">
                Perímetro exigido — Art. 40 del CPCC
              </p>
              <p className="text-sm text-amber-950 leading-relaxed">
                Según el <strong>Art. 40 del CPCC</strong>, el domicilio debe fijarse entre las calles:{' '}
                <em>{LOMAS_PERIMETRO_CALLES}</em>
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-gray-600">
                Si tu domicilio queda fuera de este perímetro, tu inscripción puede ser observada.
              </p>
              <a
                href={LOMAS_GOOGLE_MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
                data-testid="btn-google-maps-lomas"
              >
                <ExternalLink className="w-4 h-4" />
                Ver mapa interactivo en Google Maps
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
